import { useEffect, useRef, useState } from 'react';

import Lottie from 'lottie-react';
import Box from '@mui/material/Box';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useParams } from 'react-router-dom';
import error from '../../assets/error.json';
import loader from '../../assets/loader.json';
import success from '../../assets/success.json';
import { AppchargeIcon } from '../../assets/icons';
import { API_V1, getAdyenPaymentStatus } from '../../services/api.service';
import { updateAxiosSessionToken } from '../../apiUtil';
import { getQueryParam } from '../../utils/get-query-param';
import { AdyenAPMStatusResponse, EChargeStates, EPaymentStates } from '../../contracts';

const autoClose = getQueryParam('autoClose');
const shouldAutoCloseWindow = autoClose === 'true';

if (shouldAutoCloseWindow) {
  window.close();
}

export function PaymentStatus() {
  const { sessionId, orderId } = useParams();
  const [isPaymentSucceeded, setIsPaymentSucceeded] = useState<null | boolean>(null);
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const intervalRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const fetchOrder = async (orderId: string) => {
      const result = await API_V1.getOrder(orderId);
      if (result.state !== EChargeStates.PENDING && result.state !== EPaymentStates.PENDING) {
        setIsPaymentSucceeded(result.state === EChargeStates.SUCCESS);
        clearInterval(intervalRef.current);
      } else {
        intervalRef.current = setTimeout(fetchOrder, 1000, orderId);
      }
    };

    if (orderId && sessionId) {
      updateAxiosSessionToken(sessionId);
      intervalRef.current = setTimeout(fetchOrder, 1000, orderId);
    }
    return () => clearInterval(intervalRef.current);
  }, [orderId, sessionId]);

  return (
    <Stack
      height={'100dvh'}
      width={'100vw'}
      bgcolor="white"
      position={'relative'}
      zIndex={99999}
      overflow={'hidden'}
      textAlign={'center'}
      display={'flex'}
      alignItems={'center'}
      justifyContent={'center'}
    >
      <Box position={'relative'}>
        <GenericApmStatus isPaymentSucceeded={isPaymentSucceeded} />

        {/* {isPaymentMethodThatUsesBroadcastChannel ? (
          <BroadcastChannelApmStatus />
        ) : (
          <GenericApmStatus isPaymentSucceeded={isPaymentSucceeded} />
        )} */}
      </Box>
      <Stack flexDirection={'row'} alignItems={'center'} position={'absolute'} bottom={24}>
        <Box pr={1} fontSize={10}>
          {t('poweredBy')}
        </Box>
        <AppchargeIcon />
      </Stack>
    </Stack>
  );
}

function GenericApmStatus({ isPaymentSucceeded }: { isPaymentSucceeded: boolean | null }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });

  return (
    <>
      {isPaymentSucceeded !== null ? (
        <>
          <Box position={'absolute'} bottom={'60px'} right={0} left={0}>
            <Lottie
              animationData={isPaymentSucceeded ? success : error}
              style={{
                height: '160px',
              }}
              loop={false}
            />
          </Box>
          <h3
            style={{
              fontSize: '28px',
              margin: 0,
            }}
          >
            {isPaymentSucceeded ? 'Payment Successful!' : 'Payment Failed'}
          </h3>
          <h2
            style={{
              paddingTop: '8px',
              fontSize: '16px',
              fontWeight: 500,
              margin: 0,
            }}
          >
            {t('continueOnDesktop')}
          </h2>
        </>
      ) : (
        <Lottie
          animationData={loader}
          style={{
            height: '160px',
          }}
          loop={true}
        />
      )}
    </>
  );
}

function BroadcastChannelApmStatus() {
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { sessionId } = useParams();

  useEffect(() => {
    const redirectResult = getQueryParam('redirectResult');
    const paymentMethodId = getQueryParam('paymentMethodId');
    if (redirectResult && paymentMethodId) {
      updateAxiosSessionToken(sessionId as string);
      getAdyenPaymentStatus<AdyenAPMStatusResponse>(redirectResult, paymentMethodId).finally(() => {
        setIsLoading(false);
        window.close();
      });
    }
  }, []);

  return (
    <>
      {!isLoading ? (
        <Box
          sx={{
            mt: 2,
            width: '100%',
            maxWidth: 400,
            px: 2,
            fontSize: 16,
            fontWeight: 500,
            color: 'text.secondary',
            lineHeight: 1.5,
          }}
        >
          {t('closeTabReturnToCheckout')}
        </Box>
      ) : (
        <Lottie
          animationData={loader}
          style={{
            height: '160px',
          }}
          loop={true}
        />
      )}
    </>
  );
}
